import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import InAppWebView from './InAppWebView';
import { cancelIdleCallback, scheduleIdleCallback } from '../utilities/idleCallback';

interface Props {
  uri: string | null;
  enabled: boolean;
  onClose: () => void;
  onInterceptUrl?: (url: string) => boolean;
}

/**
 * Presents an eleplan.link URL only after the native app shell is settled.
 *
 * Universal links can cold-launch the app or return it from the background.
 * Mounting an iOS pageSheet in the same native transaction that replaces the
 * bootstrap screen can race the root view's modal presenter: the URL reaches
 * JavaScript without a visible viewer. Keeping the URL pending until JavaScript
 * is idle and one more event-loop turn passes gives the root navigator a
 * mounted presenter first.
 *
 * The URL itself still loads in InAppWebView so expiring and one-time links are
 * resolved by the server rather than bypassed with a direct native file route.
 */
export default function ShortLinkWebViewHost({ uri, enabled, onClose, onInterceptUrl }: Props) {
  const [appState, setAppState] = useState<AppStateStatus | null>(AppState.currentState);
  const [presentedUri, setPresentedUri] = useState<string | null>(null);
  const presentedUriRef = useRef<string | null>(null);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', setAppState);
    // Re-sample on subscribe. The initial useState read happens at first
    // render; a transition that lands between that render and this listener
    // would otherwise be lost, leaving `appState` stale for the whole session.
    setAppState(AppState.currentState);
    return () => subscription.remove();
  }, []);

  useEffect(() => {
    if (!uri) {
      presentedUriRef.current = null;
      setPresentedUri(null);
      return;
    }

    // Block only on a state that positively means "not on screen"; treat
    // everything else as foreground.
    //
    // This used to test `appState === 'active'`, which made every non-active
    // value — including `'unknown'`, a real AppStateStatus — permanently
    // fatal to a pending link, because the ONLY way out was a subsequent
    // 'change' event. A cold launch is exactly when that event is most likely
    // to have already happened: iOS starts the app `'inactive'` and promotes
    // it to `'active'` while this tree is still mounting. Miss that one
    // event and the link stayed pending for the whole app session, so the
    // app just came up on its normal launch screen and the tap looked
    // ignored. The subscribe-then-read above closes the window; this widens
    // the guard so an unexpected state cannot re-latch it.
    const isForeground = appState !== 'background' && appState !== 'inactive';
    if (!enabled || presentedUriRef.current === uri || !isForeground) return;

    // Hide a previous short link immediately while a new one waits for a safe
    // presentation point.
    if (presentedUriRef.current !== null) {
      presentedUriRef.current = null;
      setPresentedUri(null);
    }

    let cancelled = false;
    let presentationTimer: ReturnType<typeof setTimeout> | null = null;
    const idleHandle = scheduleIdleCallback(() => {
      presentationTimer = setTimeout(() => {
        if (cancelled) return;
        presentedUriRef.current = uri;
        setPresentedUri(uri);
      }, 0);
    });

    return () => {
      cancelled = true;
      cancelIdleCallback(idleHandle);
      if (presentationTimer !== null) clearTimeout(presentationTimer);
    };
  }, [appState, enabled, uri]);

  const handleClose = useCallback(() => {
    presentedUriRef.current = null;
    setPresentedUri(null);
    onClose();
  }, [onClose]);

  if (!presentedUri) return null;

  // Once presented, keep the WebView mounted while the app backgrounds so a
  // one-time link is not requested and consumed again on foreground.
  return (
    <InAppWebView
      visible={enabled && presentedUri === uri}
      uri={presentedUri}
      title="Opening link…"
      onClose={handleClose}
      onInterceptUrl={onInterceptUrl}
    />
  );
}
